import React, { memo, ReactElement, ReactNode } from 'react';
import { Icon, IconName } from 'components/Icon/Icon';
import classNames from 'classnames';
import styles from './NewButton.module.scss';

interface IProps {
	children?: ReactNode;
	className?: string;
	variant?: 'primary' | 'outline' | 'ghost' | 'shadow' | 'subtle';
	color?: 'purple' | 'blue' | 'red' | 'green' | 'gray';
	size?: 'medium' | 'small';
	type?: 'button' | 'submit';
	icon?: boolean;
	disabled?: boolean;
	loading?: boolean;
	iconLeft?: ReactElement;
	iconRight?: ReactElement;
	ariaLabel?: string;
	testId?: string;
	onClick?: () => void;
}

export const NewButton: React.FC<IProps> = memo(
	({
		children,
		className,
		variant = 'primary',
		color = 'purple',
		size = 'medium',
		type = 'button',
		icon = false,
		disabled = false,
		loading = false,
		iconLeft,
		iconRight,
		ariaLabel,
		testId,
		onClick,
	}) => {
		const containerClasses = classNames(className, styles.container, {
			[styles.sizeSmall]: size === 'small',
			[styles.variantPrimary]: variant === 'primary',
			[styles.variantOutline]: variant === 'outline',
			[styles.variantGhost]: variant === 'ghost',
			[styles.variantShadow]: variant === 'shadow',
			[styles.variantSubtle]: variant === 'subtle',
			[styles.colorPurple]: color === 'purple',
			[styles.colorBlue]: color === 'blue',
			[styles.colorRed]: color === 'red',
			[styles.colorGreen]: color === 'green',
			[styles.colorGray]: color === 'gray',
			[styles.onlyIcon]: icon,
		});

		return (
			<button
				className={containerClasses}
				type={type}
				disabled={loading || disabled}
				aria-label={ariaLabel}
				data-test={testId}
				onClick={!loading ? onClick : undefined}>
				{loading && <Icon name={IconName.loaderSpinner} className={styles.spinner} />}
				<div className={classNames(loading && 'invisible', styles.wrapper)}>
					{iconLeft}
					{children}
					{iconRight}
				</div>
			</button>
		);
	},
);
